import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../axiosConfig';
import Cookies from 'js-cookie';
import { useColorMode } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { clearTourData } from '../utils/localTourStorage'; 
import { useToast } from '@chakra-ui/react';
import { getTrialAccessSummary } from '../utils/trialAccess';
import { clearWorkbookOrganizationSurveyId } from '../components/organization/organizationWorkbookApi';

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const colorMode = useColorMode();
  const toast = useToast();

  // 🧩 Load from localStorage initially to prevent UI flickers
  const initialSettings = {
    theme: localStorage.getItem('theme') || 'light',
    language: localStorage.getItem('language') || 'en',
    emailNotifications: true
  };

  const [user, setUser] = useState(null);
  const [isHKEUser, setIsHKEUser] = useState(false);
  const [isTourUser, setIsTourUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(initialSettings);
  const [currentLanguage, setCurrentLanguage] = useState(initialSettings.language);
  const [trialAccess, setTrialAccess] = useState(getTrialAccessSummary(null));
  const isTourMode = user?.organization?.tour_mode === true;

  // 🧩 Apply theme + language on mount
  useEffect(() => {
    i18n.changeLanguage(settings.language);
    setCurrentLanguage(settings.language);
    if (colorMode.setColorMode) {
      colorMode.setColorMode(settings.theme);
    }
  }, []);

  const applyUserState = (userData) => {
    setUser(userData);
    setIsHKEUser(userData?.user_type === 'hke');
    setIsTourUser(userData?.user_type === 'tour');
    setTrialAccess(getTrialAccessSummary(userData));

    if (userData?.settings) {
      const userSettings = userData.settings;
      setSettings({
        theme: userSettings.theme ?? localStorage.getItem("theme") ?? "light",
        language: userSettings.language ?? localStorage.getItem("language") ?? "en",
        emailNotifications: userSettings.emailNotifications ?? true,
      });

      if (userSettings.language) {
        i18n.changeLanguage(userSettings.language);
        localStorage.setItem('language', userSettings.language);
        setCurrentLanguage(userSettings.language);
      }

      if (userSettings.theme) {
        colorMode.setColorMode(userSettings.theme);
        localStorage.setItem('theme', userSettings.theme);
      }

      if (userSettings.emailNotifications !== undefined) {
        localStorage.setItem('emailNotifications', userSettings.emailNotifications);
      }
    }
  };

  const clearUserSession = () => {
    setUser(null);
    setIsHKEUser(false);
    setIsTourUser(false);
    setTrialAccess(getTrialAccessSummary(null));
  };

  // 🧩 Fetch user + settings from backend
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem('hkeAuthToken');
        if (!token) {
          clearUserSession();
          setIsLoading(false);
          return;
        }

        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const response = await axios.get('/users/me');

        applyUserState(response.data);
      } catch (error) {
        console.error('Error fetching user data:', error);
        clearUserSession();
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, []);

  // 🧩 Re-apply settings when they change
  useEffect(() => {
    if (colorMode.setColorMode && settings.theme) {
      colorMode.setColorMode(settings.theme);
      localStorage.setItem('theme', settings.theme);
    }

    if (settings.language) {
      i18n.changeLanguage(settings.language);
      localStorage.setItem('language', settings.language);
    }

    if (settings.emailNotifications !== undefined) {
      localStorage.setItem('emailNotifications', settings.emailNotifications);
    }
  }, [settings]);

  useEffect(() => {
    if (isTourUser) {
      const timeout = setTimeout(() => {
        clearTourData();
        logout(); // your existing logout function
        toast.info("Tour session expired. Please start a new tour!");
      }, 30 * 60 * 1000); // 30 minutes
  
      return () => clearTimeout(timeout);
    }
  }, [isTourUser]);

  const logout = () => {
    clearWorkbookOrganizationSurveyId();
    Cookies.remove('hkeAuthToken', { path: '/' });
    Cookies.remove('tokenCreationTime', { path: '/' });
    Cookies.remove('simulatedMode', { path: '/' });
    localStorage.removeItem('hkeAuthToken');
    localStorage.removeItem('tokenCreationTime');
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem('theme');
    localStorage.removeItem('language');
    localStorage.removeItem('emailNotifications');
    window.location.href = '/login';
  };

  // 🛠 Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.patch('/users/me/change_password', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPassword,
      });

      return response.status === 200;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  };

  // 🛠 Update profile
  const updateUser = async (updatedData) => {
    try {
      const response = await axios.patch('/users/me/profile', updatedData);
      if (response.status === 200) {
        setUser(response.data.user);
        return true;
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      return false;
    }
  };

  // 🛠 Update preferences (theme/language/notifications)
  const updateSettings = async (newSettings) => {
    try {
      const response = await axios.patch('/users/me/settings', newSettings);

      if (response.status === 200) {
        const updatedSettings = { ...settings, ...newSettings };
        setSettings(updatedSettings);
        return true;
      }
    } catch (error) {
      console.error('Error updating user settings:', error);
      return false;
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isHKEUser,
        isTourUser,
        isTourMode,
        isLoading,
        settings,
        setSettings,
        currentLanguage,
        trialAccess,
        changePassword,
        updateUser,
        updateSettings,
        logout,
        applyUserState,
        clearUserSession,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
